import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { depositService } from '../services/apiServices';

// Get all deposits
export const useDeposits = (params?: any) => {
  return useQuery({
    queryKey: ['deposits', params],
    queryFn: () => depositService.getAll(params),
  });
};

// Get deposit stats
export const useDepositStats = (params?: any) => {
  return useQuery({
    queryKey: ['depositStats', params],
    queryFn: () => depositService.getStats(params),
  });
};

// Get deposits for a specific egg sale
export const useSaleDeposits = (eggSaleId: number) => {
  return useQuery({
    queryKey: ['saleDeposits', eggSaleId],
    queryFn: () => depositService.getSaleDeposits(eggSaleId),
    enabled: !!eggSaleId,
  });
};

// Create deposit mutation
export const useCreateDeposit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => depositService.create(data),
    onSuccess: (_data: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
      queryClient.invalidateQueries({ queryKey: ['saleDeposits', variables.egg_sale_id] });
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
    },
  });
};

// Delete deposit mutation
export const useDeleteDeposit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => depositService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
      queryClient.invalidateQueries({ queryKey: ['saleDeposits'] });
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
    },
  });
};

// Reverse (delete) deposits matching filters
export const useReverseDeposits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filters?: any) => depositService.reverseDeposits(filters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
      queryClient.invalidateQueries({ queryKey: ['saleDeposits'] });
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
    },
  });
};

// Correct egg_sales.amount_deposited to match actual deposit records
export const useCorrectDeposits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => depositService.correctDeposits(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
    },
  });
};

// Bulk complete pending deposits mutation
export const useBulkCompleteDeposits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filters?: any) => depositService.bulkComplete(filters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
      queryClient.invalidateQueries({ queryKey: ['depositStats'] });
      queryClient.invalidateQueries({ queryKey: ['eggSales'] });
      queryClient.invalidateQueries({ queryKey: ['eggSalesStats'] });
    },
  });
};
